// Shared text-editing chrome — the visual + gesture primitive used by both
// the COVER editor and the INTERIOR page editor. Goal: one component owns
// the "selected text" look (dashed blue outline, 3 corner dots,
// center-anchored radial resize, contentEditable inline edit), so editing a
// title on the cover and editing a caption on page 12 feel identical.
//
// 2026-06-05 mobile editor pass — second iteration:
//   - Removed the inline red trash. Deletion is owned by the heavy
//     FloatingElementToolbar on interior + the same bottom toolbar on cover,
//     not the chrome. The chrome's 22 × 22 top-right trash was missing taps
//     on iOS Safari (capture clash with the parent's drag handler).
//   - Inner box switched from `w-full h-full` → `display: inline-block`.
//     The dashed outline now hugs the rendered text (image-2 of the spec) —
//     resize visibly grows/shrinks the frame, not just the glyphs.
//   - Drag is rAF-throttled. pointermove stashes the next position in a ref;
//     a requestAnimationFrame tick reads the ref and fires onPositionChange.
//     Caps React state writes at 60Hz instead of every event (~120Hz on
//     iPhone with finger held), which is what made the previous build feel
//     stuttery.
//   - New `onDragRequest` prop: when set, the chrome SKIPS its internal
//     drag and forwards pointerdown to the parent. The interior wrapper
//     uses this to implement cross-page drag (sniffs [data-page-number]
//     siblings in the spread and calls onMovePage when the centre crosses).
//     Cover leaves it unset → keeps the in-canvas drag the chrome owns.

import { useRef, useEffect, useCallback, type ReactNode } from 'react';
import { cornerResizeScale } from '@/lib/coverResize';
import { fontStackFor } from '@/lib/textBidi';
import { bandCss } from '@/lib/textBand';

// ─── Caret helpers (Bug 2 fix, 2026-07-05) ───────────────────────────────────
// Entering edit mode used to selectNodeContents() → the whole text highlighted,
// so the next keystroke overwrote everything (customers tapping to append lost
// their text). Instead we PLACE THE CARET: at the tap point when we have
// coordinates, else collapsed to the end. Never select-all.
function placeCaretAtEnd(el: HTMLElement): void {
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false); // false = collapse to END
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  } catch { /* older browser — leave native caret */ }
}
// ITEM 6 (2026-07-06) — double-tap selects ALL the text (Docs/Canva semantics).
function selectAllContents(el: HTMLElement): void {
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  } catch { /* older browser — leave native selection */ }
}
function placeCaretAtPoint(el: HTMLElement, clientX: number, clientY: number): void {
  try {
    const d = document as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
      caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    };
    let range: Range | null = null;
    if (typeof d.caretRangeFromPoint === 'function') {
      range = d.caretRangeFromPoint(clientX, clientY);
    } else if (typeof d.caretPositionFromPoint === 'function') {
      const pos = d.caretPositionFromPoint(clientX, clientY);
      if (pos) { range = document.createRange(); range.setStart(pos.offsetNode, pos.offset); range.collapse(true); }
    }
    // Only accept a caret that actually falls inside this editable box.
    if (range && el.contains(range.startContainer)) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
  } catch { /* fall through */ }
  placeCaretAtEnd(el);
}
// Bug 1 fix — insert clipboard text as PLAIN text at the caret so the box's
// own font/size/style always wins (a pasted rich <span style> would otherwise
// override the chosen font). preventDefault stops the browser's rich paste.
function handlePlainPaste(e: React.ClipboardEvent, onAfter: () => void): void {
  e.preventDefault();
  const text = e.clipboardData.getData('text/plain');
  if (!text) return;
  try {
    // insertText inserts a plain text node at the caret, replacing any
    // selection — no HTML, no source formatting. Deprecated but universally
    // supported and the only reliable caret-aware plain insert.
    const ok = document.execCommand('insertText', false, text);
    if (!ok) throw new Error('execCommand unsupported');
  } catch {
    // Manual fallback: replace the current selection range with a text node.
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const node = document.createTextNode(text);
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }
  onAfter(); // sync state from the box's textContent
}

export interface TextOverlayChromeProps {
  /** DESIGN SYSTEM STEP 2 — degrees, clockwise, about the box centre (the
   *  print turns the text raster the same way). */
  rotation?: number;
  /** Position + size in percent of the parent container (0..100). */
  x: number;
  y: number;
  width: number;
  height: number;

  /** The text content. */
  text: string;

  /** Text styling — piped straight to the inner span. */
  fontFamily?: string;
  fontSizePx: number | string;
  /** The print's letter cut for optical-size fonts (pdfTextLayout.printOpticalSize). */
  fontVariationSettings?: string;
  fontWeight?: number | string;
  fontStyle?: string;
  textDecoration?: string;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  lineHeight?: number | string;
  /** A thin outline around each letter (captions on photos, #WA-269649):
   *  `outlineWidth` × the font size, stroked UNDER the fill — the PDF raster
   *  draws the same (pdfGenerator.rasterizeTextElement). */
  outlineColor?: string;
  outlineWidth?: number;
  /** The book-colour band behind a caption, painted on the WHOLE element box
   *  (the print draws the same rectangle — lib/textBand.ts). */
  bandFill?: string;
  bandOpacity?: number;
  /** The box still holds its placeholder (« Votre texte »): entering edit mode
   *  selects it whole, so the first keystroke or paste REPLACES it instead of
   *  being appended to it (#WA-269649 p37: « Votre textePequeños pasos… »). */
  selectAllOnEdit?: boolean;

  /** Selection / edit state — owned by the parent. */
  isSelected: boolean;
  isEditing: boolean;
  /** Locked elements still select (so the user can unlock) but the chrome
   *  hides the resize dots + ignores drag attempts. */
  locked?: boolean;

  /** Fires on plain tap of the chrome. `shiftKey` for desktop add-to-select. */
  onSelect: (shiftKey: boolean) => void;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onTextChange: (next: string) => void;
  /** Fires during drag with the NEW (x, y) in percent of the parent. The
   *  chrome rAF-throttles these to ~60Hz; surface receives at most one
   *  callback per animation frame. Already clamped to keep the box on-
   *  canvas. NOT called when `onDragRequest` is set (parent owns drag). */
  onPositionChange: (x: number, y: number) => void;
  /** Fires during corner-resize with a multiplier vs the size at gesture
   *  start. See phase semantics in the audit doc. */
  onResize: (scaleRatio: number, phase: 'start' | 'move' | 'end') => void;

  /** Fires true on drag-start, false on drag-end. */
  onDragStateChange?: (isDragging: boolean) => void;

  /** Optional drag override. When set, the chrome's outer pointerdown
   *  delegates to this callback INSTEAD of running its internal in-canvas
   *  drag. Interior uses this for cross-page text drag; cover leaves it
   *  unset so the chrome's own in-canvas drag still runs. */
  onDragRequest?: (e: React.PointerEvent) => void;

  /** Optional extra UI rendered at the top of the dashed box — used by the
   *  interior surface to expose rotate / lock / duplicate / "more". */
  extras?: ReactNode;
  /** Rendered as `data-element-id` on the box — the editor's floating text
   *  toolbar finds the element on screen by it (placed above / below it). */
  elementId?: string;
}

const DASH_COLOR = '#3b82f6';      // blue-500
const HANDLE_BG = '#3b82f6';

export function TextOverlayChrome(props: TextOverlayChromeProps) {
  const {
    x, y, width, height,
    text, fontFamily, fontSizePx, fontVariationSettings, fontWeight, fontStyle, textDecoration,
    color, textAlign = 'center', lineHeight = 1.3, outlineColor, outlineWidth, bandFill, bandOpacity, selectAllOnEdit,
    isSelected, isEditing, locked,
    onSelect, onStartEditing, onStopEditing, onTextChange,
    onPositionChange, onResize, onDragStateChange,
    onDragRequest,
    extras,
    rotation,
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const editableRef = useRef<HTMLSpanElement | null>(null);

  // ─── Inline text editing ───────────────────────────────────────────────
  useEffect(() => {
    const el = editableRef.current;
    if (!el) return;
    if ((el.textContent ?? '') !== text) el.textContent = text;
  }, [text, isEditing]);

  // A tap-to-edit that already placed the caret at the tap point sets this so
  // the isEditing effect below doesn't clobber it with caret-at-end.
  const caretPlacedRef = useRef(false);
  // Mobile smart-scroll memory: every scrollable ancestor's position at the
  // moment typing started, so the view can glide BACK when typing ends.
  const savedScrollsRef = useRef<Array<{ el: Element; top: number; left: number }> | null>(null);
  useEffect(() => {
    if (!isEditing) {
      // Typing ended → mobile "zoom back out": restore the exact view the
      // customer had before the keyboard scroll, so photos/pages end up
      // precisely where they were. No-op on desktop (nothing was saved).
      const saved = savedScrollsRef.current;
      savedScrollsRef.current = null;
      if (saved) {
        for (const s of saved) {
          try { s.el.scrollTo({ top: s.top, left: s.left, behavior: 'smooth' }); } catch { /* defensive */ }
        }
      }
      return;
    }
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    // Bug 2 — caret, NOT select-all. If the tap handler already positioned the
    // caret at the tap point, keep it; otherwise (desktop dbl-click, programmatic
    // entry) collapse to the end so typing appends.
    if (caretPlacedRef.current) {
      caretPlacedRef.current = false;
    } else if (selectAllOnEdit) {
      selectAllContents(el);
    } else {
      placeCaretAtEnd(el);
    }
    // VIEW-SCROLL POLICY (2026-07-08, per Yahya): DESKTOP never scrolls on
    // typing entry — the old auto-centering made the whole spread (and the
    // photos on it) visibly drift up to ~270px the moment text was tapped,
    // reading as "the photo moved". MOBILE keeps the smart behaviour (the
    // keyboard covers half the screen, so bring the text into view), and the
    // exit branch above scrolls BACK once typing ends — net zero movement.
    try {
      if (window.matchMedia?.('(pointer: coarse)').matches) {
        const saved: Array<{ el: Element; top: number; left: number }> = [];
        let p: HTMLElement | null = containerRef.current?.parentElement ?? null;
        while (p) {
          if (p.scrollHeight > p.clientHeight + 4 || p.scrollWidth > p.clientWidth + 4) {
            saved.push({ el: p, top: p.scrollTop, left: p.scrollLeft });
          }
          p = p.parentElement;
        }
        savedScrollsRef.current = saved;
        containerRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    } catch { /* defensive */ }
  }, [isEditing]);

  // ─── Drag (move the box) ───────────────────────────────────────────────
  // rAF-throttled — pointermove stashes the next position in a ref and only
  // emits at most one onPositionChange per animation frame. Without this,
  // a 120Hz iPhone fires React state updates faster than React can flush
  // and the drag stutters (audit Fix 3, 2026-06-05).
  const pendingPosRef = useRef<{ x: number; y: number } | null>(null);
  const rafIdRef = useRef<number | null>(null);
  // Set when a drag STARTED FROM EDIT MODE consumed the gesture — the trailing
  // click (browser fires it after pointerup) must not re-enter editing.
  const suppressClickRef = useRef(false);

  // Internal mover (no onDragRequest — e.g. the cover). Extracted so both the
  // normal selected-drag AND the edit-escape drag below share it.
  const startInternalMove = useCallback((startClientX: number, startClientY: number, captureEl?: HTMLElement | null, pointerId?: number) => {
    const parent = containerRef.current?.parentElement;
    const parentRect = parent?.getBoundingClientRect();
    const cw = parentRect?.width ?? 1;
    const ch = parentRect?.height ?? 1;
    const startX = x;
    const startY = y;
    if (captureEl && pointerId != null) {
      try { captureEl.setPointerCapture(pointerId); } catch { /* defensive */ }
    }
    onDragStateChange?.(true);

    const flush = () => {
      rafIdRef.current = null;
      const p = pendingPosRef.current;
      if (p) onPositionChange(p.x, p.y);
    };

    const onMove = (ev: PointerEvent) => {
      const dxPct = ((ev.clientX - startClientX) / cw) * 100;
      const dyPct = ((ev.clientY - startClientY) / ch) * 100;
      const nextX = Math.max(0, Math.min(100 - width,  startX + dxPct));
      const nextY = Math.max(0, Math.min(100 - height, startY + dyPct));
      pendingPosRef.current = { x: nextX, y: nextY };
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(flush);
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      // Flush any pending rAF so the final position lands in state.
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (pendingPosRef.current) {
        onPositionChange(pendingPosRef.current.x, pendingPosRef.current.y);
        pendingPosRef.current = null;
      }
      onDragStateChange?.(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [x, y, width, height, onPositionChange, onDragStateChange]);

  // EDIT-ESCAPE DRAG (2026-07-08): customers grab the text WHILE the caret /
  // toolbar is open (one tap opens editing, so almost every drag attempt
  // happens in edit mode) — nothing handled the gesture, so the browser
  // scrolled the page / started a text selection instead ("the whole page
  // goes up"). Now: a pointerdown in edit mode arms a watcher; past an 8px
  // threshold it's a MOVE — exit editing, kill the selection, and hand the
  // SAME gesture to the normal drag path (parent's cross-page/bleed drag on
  // interior pages, the internal mover on the cover). Under the threshold
  // it's a tap — caret placement and typing behave exactly as before.
  const startEditEscapeWatch = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const downX = e.clientX;
    const downY = e.clientY;
    const pid = e.pointerId;
    const ptype = e.pointerType;
    const container = containerRef.current;
    const THRESHOLD_PX = 8;
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pid) return;
      if (Math.hypot(ev.clientX - downX, ev.clientY - downY) < THRESHOLD_PX) return;
      cleanup();
      suppressClickRef.current = true;
      // UNIFIED STATE (2026-07-08): the drag NO LONGER exits typing — focus
      // (and the mobile keyboard) survive the move so the customer can drop
      // the text and keep typing immediately. Native selection is what used
      // to fight the move, so it's disabled just for the drag's duration and
      // the caret is restored at the drop.
      const span = editableRef.current;
      try { window.getSelection()?.removeAllRanges(); } catch { /* defensive */ }
      if (span) {
        span.style.userSelect = 'none';
        (span.style as unknown as Record<string, string>).webkitUserSelect = 'none';
      }
      const restoreCaret = (ev2: PointerEvent) => {
        if (ev2.pointerId !== pid) return;
        window.removeEventListener('pointerup', restoreCaret);
        window.removeEventListener('pointercancel', restoreCaret);
        const el = editableRef.current;
        if (!el) return;
        el.style.userSelect = '';
        (el.style as unknown as Record<string, string>).webkitUserSelect = '';
        try { el.focus({ preventScroll: true }); placeCaretAtEnd(el); } catch { /* defensive */ }
      };
      window.addEventListener('pointerup', restoreCaret);
      window.addEventListener('pointercancel', restoreCaret);
      if (onDragRequest) {
        // Parent-managed drag. The original React event is finished, so pass a
        // minimal synthetic one anchored at the ORIGINAL down point — the drag
        // math is delta-based from there, keeping the box glued to the finger.
        onDragRequest({
          clientX: downX,
          clientY: downY,
          pointerId: pid,
          pointerType: ptype,
          button: 0,
          shiftKey: false,
          currentTarget: container,
          target: container,
          stopPropagation: () => {},
          preventDefault: () => {},
        } as unknown as React.PointerEvent);
      } else {
        startInternalMove(downX, downY, container, pid);
      }
    };
    const onUp = (ev: PointerEvent) => { if (ev.pointerId === pid) cleanup(); };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [onDragRequest, startInternalMove]);

  const editableNow = isEditing && !locked;
  const handleDragStart = useCallback((e: React.PointerEvent) => {
    // Edit mode: don't hijack immediately — the watcher decides tap vs move.
    // (Covers grabs on the box PADDING; grabs on the text itself arm the same
    // watcher from the span's own pointerdown.) preventDefault keeps the span
    // FOCUSED — a mousedown on the (non-focusable) padding div would blur the
    // text and exit typing, breaking the unified type+move state.
    if (editableNow) {
      e.stopPropagation();
      e.preventDefault();
      startEditEscapeWatch(e);
      return;
    }
    // External drag override (cross-page on interior) — let the parent
    // take over the gesture entirely. Chrome stays out of the way.
    if (onDragRequest) {
      onDragRequest(e);
      return;
    }

    if (!isSelected || isEditing || locked) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    startInternalMove(e.clientX, e.clientY, target, e.pointerId);
  }, [editableNow, startEditEscapeWatch, onDragRequest, isSelected, isEditing, locked, startInternalMove]);

  // ─── Resize via corner handles ────────────────────────────────────────
  const handleResizeStart = useCallback((e: React.PointerEvent) => {
    if (locked) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    try { target.setPointerCapture(e.pointerId); } catch { /* defensive */ }

    const rect = (boxRef.current ?? containerRef.current)?.getBoundingClientRect();
    const center = rect
      ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      : { x: e.clientX, y: e.clientY };
    const start = { x: e.clientX, y: e.clientY };

    onResize(1, 'start');

    let lastRatio = 1;
    const onMove = (ev: PointerEvent) => {
      lastRatio = cornerResizeScale(center, start, { x: ev.clientX, y: ev.clientY }, 1, 0.1, 10);
      onResize(lastRatio, 'move');
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      onResize(lastRatio, 'end');
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [locked, onResize]);

  // ─── Render ────────────────────────────────────────────────────────────

  const editable = isEditing && !locked;

  // Outer is the % bbox of the element — drives positioning, NOT the
  // visible frame. The inner inline-block hugs the actual rendered text
  // and is where the dashed outline + corner handles live. This is what
  // the cover already does (CoverThumbnail.TextOverlay) — copying its
  // model fixes the "frame stays static while text grows" bug.
  return (
    <div
      ref={containerRef}
      data-text-editing={isEditing ? 'true' : undefined}
      data-element-id={props.elementId}
      className="absolute select-none flex"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${width}%`,
        height: `${height}%`,
        // Z-ORDER (2026-07-06): text sits ABOVE a templated page's decorative
        // overlay PNG (zIndex 1000) so text added over a photo/template shows ON
        // TOP, not behind the art. Matches the free photo/element z-order.
        zIndex: isSelected ? 1020 : 1010,
        cursor: editable ? 'text' : (isSelected && !locked ? 'move' : 'pointer'),
        alignItems: 'center',
        justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
        // TOUCH DRAG FIX (2026-07-08): without this, a vertical drag on the text
        // was consumed by the page's `pan-y` scroll (the whole spread scrolled
        // instead of the text moving). Every other draggable element in the
        // editor already sets touch-action:none; text was the exception.
        // ALWAYS none — including edit mode: the edit-escape watcher owns the
        // gesture there (tap = caret, move = drag the box); if the browser were
        // allowed to pan it would steal the gesture before the watcher fires.
        touchAction: 'none',
        // STEP 2 — rotated text, about the stored box centre (print: same).
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: 'center',
      }}
      onPointerDown={handleDragStart}
      onClick={(e) => {
        e.stopPropagation();
        // A drag that started from edit mode just consumed this gesture — the
        // browser still fires a trailing click; swallow it so the box doesn't
        // instantly re-enter editing after being moved.
        if (suppressClickRef.current) { suppressClickRef.current = false; return; }
        if (locked) { onSelect(e.shiftKey); return; }
        if (isEditing) return;
        // One tap = edit. CRITICAL for iOS: make the span editable and focus it
        // SYNCHRONOUSLY inside this tap gesture so Safari raises the keyboard —
        // a focus deferred to the isEditing effect is ignored by iOS outside a
        // user gesture. React state catches up via onStartEditing() below.
        const el = editableRef.current;
        if (el) {
          try {
            el.contentEditable = 'true';
            el.focus({ preventScroll: true });
            // Bug 2 — put the caret WHERE THE CUSTOMER TAPPED (not select-all),
            // so tapping at the end continues typing there. The isEditing effect
            // sees caretPlacedRef and won't override it. A box still holding its
            // placeholder is selected whole instead: typing replaces it.
            if (selectAllOnEdit) selectAllContents(el);
            else placeCaretAtPoint(el, e.clientX, e.clientY);
            caretPlacedRef.current = true;
          } catch { /* defensive — older browser range/focus API */ }
        }
        onSelect(false);
        onStartEditing();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (locked) return;
        // ITEM 6 — double-tap = select ALL (single tap already placed the caret
        // at the tap point via onClick). caretPlacedRef stops the isEditing
        // effect from collapsing this selection to the end.
        const el = editableRef.current;
        if (el) {
          try {
            el.contentEditable = 'true';
            el.focus({ preventScroll: true });
            selectAllContents(el);
            caretPlacedRef.current = true;
          } catch { /* defensive */ }
        }
        onSelect(false);
        onStartEditing();
      }}
    >
      {bandCss({ bandFill, bandOpacity }) && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: bandCss({ bandFill, bandOpacity }) }}
        />
      )}
      <div
        ref={boxRef}
        className="relative"
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          outline: isSelected ? `2px dashed ${DASH_COLOR}` : 'none',
          outlineOffset: 2,
        }}
      >
        <span
          ref={editableRef}
          className="outline-none"
          contentEditable={editable}
          suppressContentEditableWarning
          spellCheck={false}
          onInput={() => onTextChange(editableRef.current?.textContent ?? '')}
          onPaste={(e) => handlePlainPaste(e, () => onTextChange(editableRef.current?.textContent ?? ''))}
          onPointerDown={(e) => {
            if (!editable) return;
            // Edit mode: arm the tap-vs-move watcher instead of letting the
            // browser own the gesture (it used to scroll the page / drag a
            // text selection). Tap → caret as before; move → drag the box.
            e.stopPropagation();
            startEditEscapeWatch(e);
          }}
          onBlur={() => onStopEditing()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.shiftKey || e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              (e.target as HTMLElement).blur();
            } else if (e.key === 'Escape') {
              (e.target as HTMLElement).blur();
            }
          }}
          style={{
            display: 'inline-block',
            // Arabic letters in a NAMED Arabic face, the same stack the print
            // raster uses (lib/textBidi.ts, #WA-269649).
            fontFamily: fontStackFor(fontFamily, text),
            fontSize: typeof fontSizePx === 'number' ? `${fontSizePx}px` : fontSizePx,
            fontVariationSettings,
            fontWeight: fontWeight ?? 400,
            fontStyle: fontStyle ?? 'normal',
            textDecoration: textDecoration ?? 'none',
            color: color ?? '#0F172A',
            textAlign: textAlign,
            lineHeight: lineHeight,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            overflow: 'visible',
            // Each line the person typed reads in its own direction: an Arabic
            // line under a Spanish one ends on its LEFT, full stop included.
            // The print raster does the same per paragraph (layoutTextLines).
            unicodeBidi: 'plaintext',
            ...(outlineColor && (outlineWidth ?? 0) > 0 ? {
              WebkitTextStroke: `${2 * (outlineWidth as number)}em ${outlineColor}`,
              paintOrder: 'stroke fill',
            } : {}),
          }}
        />

        {/* Selected chrome: corner handles + extras slot. Trash is
            intentionally NOT here — the heavy FloatingElementToolbar (or
            the cover's bottom bar) owns deletion. UNIFIED STATE (2026-07-08):
            handles now show WHILE TYPING too — there is no separate
            "move mode" anymore; resize handles stopPropagation so they never
            disturb the caret. */}
        {isSelected && (
          <>
            {extras && (
              <div
                className="absolute"
                style={{ top: -10, left: -10, zIndex: 35 }}
                onPointerDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); }}
              >
                {extras}
              </div>
            )}
            {!locked && (
              <>
                <CornerHandle pos="nw" onPointerDown={handleResizeStart} />
                <CornerHandle pos="ne" onPointerDown={handleResizeStart} />
                <CornerHandle pos="sw" onPointerDown={handleResizeStart} />
                <CornerHandle pos="se" onPointerDown={handleResizeStart} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CornerHandle({
  pos,
  onPointerDown,
}: {
  pos: 'nw' | 'ne' | 'sw' | 'se';
  onPointerDown: (e: React.PointerEvent) => void;
}) {
  const style: React.CSSProperties = {
    position: 'absolute',
    width: 12,
    height: 12,
    borderRadius: 9999,
    background: HANDLE_BG,
    border: '2px solid #fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    zIndex: 33,
    touchAction: 'none',
    cursor: pos === 'nw' || pos === 'se' ? 'nwse-resize' : 'nesw-resize',
  };
  if (pos === 'nw') { style.top = -6; style.left = -6; }
  if (pos === 'ne') { style.top = -6; style.right = -6; }
  if (pos === 'sw') { style.bottom = -6; style.left = -6; }
  if (pos === 'se') { style.bottom = -6; style.right = -6; }
  return (
    <div
      data-resize-handle=""
      style={style}
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
    />
  );
}
